import { useState, useEffect, useCallback, useMemo } from 'react';

import FooterSuporte from 'src/components/FooterSuporte';
import { useUserContext } from 'src/contexts/UserContext';

import { getPromotionMessage, formatNumber } from 'src/services/utils';
import { createReferralLink } from 'src/utils/createReferralLink';

import ClientsTable from './InnerComponents';

import { CustomersService } from 'src/services/Customers';

import './style.css';
import { useFirstBuyCouponQuery } from 'src/services/Promotions/queries';
import { PageLayout } from 'src/components/PageLayout';

export function Customers() {
  const [activeCustomers, setactiveCustomers] = useState();
  const [inactiveCustomers, setInactiveCustomers] = useState();
  const [optionCard, setOptionCard] = useState('ativos');
  const [fields, setFields] = useState({
    search: '',
  });
  const [filteredCustomers, setFilteredCustomers] = useState();

  const { userData } = useUserContext();
  const {
    data: { code: firstBuyCoupon },
  } = useFirstBuyCouponQuery();

  const encodedReferralLink = useMemo(() => {
    if (userData?.Link_da_Loja) {
      return encodeURIComponent(userData.Link_da_Loja);
    }
    const referralLink = createReferralLink(userData.Multivendor);

    return encodeURIComponent(referralLink);
  }, [userData?.Link_da_Loja, userData?.Multivendor]);

  const promotionMessage = useMemo(
    () => getPromotionMessage({ link: encodedReferralLink, firstBuyCoupon }),
    [encodedReferralLink, firstBuyCoupon],
  );

  const getCustomers = useCallback(async () => {
    try {
      const customers = await CustomersService.getCustomers();
      const dataActive =
        customers?.data.filter(customer => customer.orders_amount) || [];
      const dataInactive =
        customers?.data.filter(customer => !customer.orders_amount) || [];
      setactiveCustomers(dataActive);
      setInactiveCustomers(dataInactive);
      setFilteredCustomers(dataActive);
    } catch (error) {
      console.log(error);
      setactiveCustomers([]);
      setInactiveCustomers([]);
      setFilteredCustomers([]);
    }
  }, []);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  const searchCustomers = (customers, fields) => {
    const searchLower = fields.search?.toLowerCase() || '';
    if (!customers) return [];
    if (!searchLower.length) return customers;

    const filteredCustomers = customers.filter(customer => {
      const customerName = customer?.name?.toLowerCase() || '';
      const customerLocation = customer?.location?.toLowerCase() || '';
      const includesCustomerName = customerName.includes(searchLower);
      const includesCustomerLocation = customerLocation.includes(searchLower);

      return includesCustomerName || includesCustomerLocation;
    });
    return filteredCustomers;
  };

  const updateReminderDate = async retailer_id => {
    try {
      await CustomersService.updateReminderDate(retailer_id);
      getCustomers();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    if (optionCard === 'ativos') {
      const customers = searchCustomers(activeCustomers, fields);
      setFilteredCustomers(customers);
    } else if (optionCard === 'inativos') {
      const customers = searchCustomers(inactiveCustomers, fields);
      setFilteredCustomers(customers);
    }
  }, [fields, optionCard, activeCustomers, inactiveCustomers]);

  return (
    <PageLayout className="overflow-x-hidden">
      <div className="p-md-3 p-2" id="client-wrapper">
        <div className="row">
          <div className="col-6">
            <h1 className="title-section display-5 mb-4 fw-light">Clientes</h1>
          </div>
          <div className="col-6 mb-4 align-self-center">
            <a
              className="btn border btn-dark border-2 border-dark fw-bold float-end indicar-lojista"
              href="/indicar"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="indicar-lojista__title">Indicar lojistas</div>
                <i className="fas fa-arrow-right" />
              </div>
            </a>
          </div>
        </div>
        <div className="mb-4 mt-2 d-flex flex-column">
          <div className="col-12 p-0 mb-4">
            <div className="search-bar px-3">
              <input
                type="text"
                name="search"
                className="py-4"
                id="search"
                placeholder="Buscar por nome ou região"
                value={fields.search}
                onChange={e => {
                  setFields({ ...fields, [e.target.name]: e.target.value });
                }}
              />
              {fields.search.length === 0 ? (
                <i className="fas fa-search" />
              ) : (
                <i
                  className="fas fa-times-circle"
                  onClick={() => setFields({ ...fields, search: '' })}
                />
              )}
            </div>
          </div>
        </div>
        <div className="py-4 pt-0 d-flex justify-content-center">
          <div className="row w-100">
            <button
              className={
                optionCard === 'ativos'
                  ? 'col-6 border d-flex justify-content-center p-3 active-card'
                  : 'col-6 border  d-flex justify-content-center p-3 no-active-card'
              }
              onClick={() => setOptionCard('ativos')}
            >
              {activeCustomers ? (
                <div className="d-flex align-items-start flex-column">
                  <div>Compradores</div>
                  <div className="fw-bold clientes-text-length">
                    {activeCustomers?.length &&
                      formatNumber(activeCustomers.length)}
                  </div>
                </div>
              ) : (
                <div className="spinner-border m-5" role="status" />
              )}
            </button>
            <button
              className={
                optionCard === 'inativos'
                  ? 'col-6 border  d-flex justify-content-center p-3 active-card'
                  : 'col-6 border  d-flex justify-content-center p-3 no-active-card'
              }
              onClick={() => setOptionCard('inativos')}
            >
              {inactiveCustomers ? (
                <div className="d-flex align-items-start flex-column">
                  <div>Não compradores</div>
                  <div className="fw-bold clientes-text-length">
                    {inactiveCustomers?.length &&
                      formatNumber(inactiveCustomers.length)}
                  </div>
                </div>
              ) : (
                <div className="spinner-border m-5" role="status" />
              )}
            </button>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mt-4">
            <ClientsTable
              optionCard={optionCard}
              filterClientes={filteredCustomers}
              clientesAtivos={activeCustomers}
              textLinkFornecedor={promotionMessage}
              updatingReminderDate={updateReminderDate}
            />
          </div>
        </div>
      </div>
      <FooterSuporte />
    </PageLayout>
  );
}
